import { removeTrackingScripts } from '@/helpers/utils/trackingCleanup';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

const COOKIE_CONSENT_KEYNAME = 'rejectTrackingCookies';

type AppConfig = {
  isMobileMenuOpen: boolean;
  isCookieConsentRejected: boolean | undefined;
  applicationHeaders?: Record<string, string | null>;
};

const getCookieConsentFromLocalStorage = (): boolean | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    const stored = window.localStorage.getItem(COOKIE_CONSENT_KEYNAME);
    if (stored === null) return undefined;
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return undefined;
  } catch (error) {
    console.error('Error reading cookie consent from localStorage:', error);
    return undefined;
  }
};

const setCookieConsentInLocalStorage = (value: boolean): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(COOKIE_CONSENT_KEYNAME, String(value));
  } catch (error) {
    console.error('Error writing cookie consent to localStorage:', error);
  }
};

// Async thunk for loading cookie consent from localStorage
export const loadCookieConsentAsync = createAsyncThunk(
  'appConfig/loadCookieConsent',
  async (): Promise<boolean | undefined> => {
    if (typeof window === 'undefined') return undefined;
    try {
      if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return false;

      return getCookieConsentFromLocalStorage();
    } catch (error) {
      console.error('Failed to load cookie consent from localStorage:', error);
      return undefined;
    }
  }
);

// Async thunk for setting cookie consent with localStorage
export const setCookieConsentAsync = createAsyncThunk(
  'appConfig/setCookieConsent',
  async (isCookieConsentRejected: boolean) => {
    if (typeof window === 'undefined') {
      console.log('Window is undefined, cannot save cookie consent');
      return isCookieConsentRejected;
    }

    try {
      console.log('Setting cookie consent in localStorage:', isCookieConsentRejected);
      setCookieConsentInLocalStorage(isCookieConsentRejected);
      if (isCookieConsentRejected) {
        removeTrackingScripts();
      }

      return isCookieConsentRejected;
    } catch (error) {
      console.error('Failed to save cookie consent to localStorage:', error);
      // Still return the value so the state gets updated even if storage fails
      return isCookieConsentRejected;
    }
  }
);

const initialState = {
  isMobileMenuOpen: false,
  isCookieConsentRejected: undefined,
  applicationHeaders: undefined,
} as AppConfig;

const appConfig = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {
    setIsMobileMenuOpen(state, { payload: isMobileMenuOpen }: PayloadAction<boolean>) {
      state.isMobileMenuOpen = isMobileMenuOpen;
    },
    setIsCookieConsentGranted(state, { payload: isCookieConsentRejected }: PayloadAction<boolean>) {
      state.isCookieConsentRejected = isCookieConsentRejected;
    },
    setApplicationHeaders(state, { payload: applicationHeaders }: PayloadAction<Record<string, string | null>>) {
      state.applicationHeaders = applicationHeaders;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadCookieConsentAsync.fulfilled, (state, action) => {
        state.isCookieConsentRejected = action.payload;
      })
      .addCase(setCookieConsentAsync.fulfilled, (state, action) => {
        state.isCookieConsentRejected = action.payload;
      });
  },
});

export const { setIsMobileMenuOpen, setIsCookieConsentGranted, setApplicationHeaders } = appConfig.actions;
export default appConfig;
